import React, { memo, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Map,
  useMap,
} from '@vis.gl/react-google-maps';
import { useAmenityControls } from './useAmenityControls';
import { useTenantPropertiesData, useThingsToDoData, useZctaOverlayData } from './useMapDataOverlays';
import { useMapReadiness } from './useMapReadiness';
import { useIsDarkTheme } from './useIsDarkTheme';
import { MapLoadReporter, MapClickHandler, SyncMapType, SyncColorScheme } from './MapLifecycleAdapters';
import LocationPickPopup, { LOCATION_PICK_POPUP_KEY } from './LocationPickPopup';
import MapControlsBar from './MapControlsBar';

import {
  AmenityMarkersLayer,
  AddressMarkersLayer,
  HotSpringLayer,
  ListingMarkersLayer,
  SkiResortLayer,
  TenantPropertiesLayer,
} from './MapMarkerLayers';
import ZctaCrimeLayer from './ZctaCrimeLayer';
import BikeLayer from './BikeLayer';
import BikeLoadingBar from './BikeLoadingBar';
import {
  isValidCenter,
  normalizeArrayProp,
  resolveGoogleMapTypeId,
  resolveMapColorScheme,
  selectPrimaryCenter,
  toLatLng,
  toRoundedOsmGridCenter,
} from './mapViewUtils';
import {
  MAP_DEFAULT_ZOOM,
  MAP_ID,
} from './mapConfig';
import './MapView.css';

function FocusedMarkerController({ focusedMarker }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !focusedMarker?.lat || !focusedMarker?.lng) return;
    map.panTo({ lat: focusedMarker.lat, lng: focusedMarker.lng });
    const zoom = map.getZoom?.() ?? MAP_DEFAULT_ZOOM;
    if (zoom < 13) map.setZoom(13);
    // ts changes on every tap in the side list, so re-tapping the same place pans again.
  }, [map, focusedMarker?.lat, focusedMarker?.lng, focusedMarker?.ts]);

  return null;
}

// ---------------------------------------------------------------------------
// MapView - public component.
// APIProvider loads the Maps JS API (including Places lib) once for the app.
// All child components that use useMap() must be inside <Map>.
// ---------------------------------------------------------------------------
function MapViewComponent({
  workCenter,
  homeCenter,
  workAddress,
  homeAddress,
  stores: storesProp = [],
  gyms: gymsProp = [],
  showStoreMarkers = true,
  showGymMarkers = true,
  showThingsToDo = false,
  ffListings: ffListingsProp = [],
  showFFMarkers = true,
  zctaLayerOpacity = 0.45,
  mapLayer = 'default',
  onMapLayerChange = () => {},
  onOpenFilters = () => {},
  onMapReady = () => {},
  onZctaReady = () => {},
  onThingsToDoReady = () => {},
  onListingsRenderProgress = () => {},
  zctaCrimeData: zctaCrimeDataProp,
  coverageAreaScores = null,
  showCrimeLayer = true,
  showCrimeControls = true,
  hideSafetyScore = false,
  hideAffordabilityScore = false,
  hideLivabilityScore = false,
  onZctaUseLocation = null,
  onShowCrimeLayerChange = () => {},
  onZctaLayerOpacityChange = () => {},
  showRentals = true,
  onShowRentalsChange = () => {},
  onShowFFMarkersChange = () => {},
  /** When true (build-time env), map Icons menu can show the custom CSV listings toggle. */
  tenantListingsFeatureEnabled = false,
  showTenantCustomListings = false,
  onShowTenantCustomListingsChange = () => {},
  inactiveTenantListings = [],
  onToggleTenantListingInactive = () => {},
  favoriteTenantListings = [],
  onToggleTenantListingFavorite = () => {},
  searchKey = 0,
  focusedMarker = null,
}) {
  const stores = useMemo(() => normalizeArrayProp(storesProp), [storesProp]);
  const gyms = useMemo(() => normalizeArrayProp(gymsProp), [gymsProp]);
  const ffListings = useMemo(() => normalizeArrayProp(ffListingsProp), [ffListingsProp]);
  const isDarkTheme = useIsDarkTheme();
  const hasWork = isValidCenter(workCenter);
  const hasHome = isValidCenter(homeCenter);
  const hasWorkHome = hasWork || hasHome;

  const [openMarkerKey, setOpenMarkerKey] = useState(null);
  const [showBikeLayer, setShowBikeLayer] = useState(false);
  const [bikeLoadingProgress, setBikeLoadingProgress] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef(null);

  const [pickedPoint, setPickedPoint] = useState(null);
  const openMarkerKeyRef = useRef(openMarkerKey);
  openMarkerKeyRef.current = openMarkerKey;

  const closePopup = useCallback(() => setOpenMarkerKey(null), []);

  // Tapping the map: if a popup is open, just close it (mobile tap-to-dismiss).
  // Otherwise show a popup at that spot so the user can choose "Use this Location".
  const handleMapTap = useCallback((lat, lng) => {
    if (openMarkerKeyRef.current) {
      setOpenMarkerKey(null);
      return;
    }
    setPickedPoint({ lat, lng });
    setOpenMarkerKey(LOCATION_PICK_POPUP_KEY);
  }, []);

  const handleBikeLoadingProgress = useCallback((p) => {
    setBikeLoadingProgress(p);
  }, []);

  // Round center to 0.1° grid so minor re-renders don't re-trigger queries.
  const osmCenter = selectPrimaryCenter(workCenter, homeCenter);
  const { osmLat, osmLng } = toRoundedOsmGridCenter(osmCenter);

  const { hotSprings, skiResorts } = useThingsToDoData({
    showThingsToDo,
    osmLat,
    osmLng,
    onThingsToDoReady,
    searchKey,
  });
  const shouldLoadTenantProperties = tenantListingsFeatureEnabled && showRentals && showTenantCustomListings;
  const effectiveShowFFMarkers = showRentals && showFFMarkers;
  const tenantProperties = useTenantPropertiesData(shouldLoadTenantProperties);

  const { zctaData, fullGeoJson } = useZctaOverlayData({ zctaCrimeDataProp, workCenter, homeCenter });

  const { handleMapReady } = useMapReadiness({
    fullGeoJson,
    onMapReady,
    onZctaReady,
    searchKey,
  });

  const hotSpringCount = hotSprings?.length ?? 0;
  const skiResortCount = skiResorts?.length ?? 0;

  useEffect(() => {
    const onChange = () => {
      const active = document.fullscreenElement === rootRef.current;
      setIsFullscreen(active);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Leaving /search while fullscreen is active can leave the browser fullscreen layer
  // covering the next route (Profile, Sign in, etc.) on some browsers.
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined' && document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (!focusedMarker?.markerKey) return;
    setOpenMarkerKey(focusedMarker.markerKey);
  }, [focusedMarker?.markerKey, focusedMarker?.ts]);

  const {
    amenityVisibility,
    handleAmenityToggle,
    amenityOptions,
    amenityMarkerGroups,
  } = useAmenityControls({
    stores,
    gyms,
    hotSpringCount,
    skiResortCount,
    showThingsToDo,
    showStoreMarkers,
    showGymMarkers,
  });

  if (!hasWorkHome) {
    return <div className="map-view map-view--loading">Loading map…</div>;
  }

  const handleFullscreenToggle = async () => {
    if (!rootRef.current) return;
    if (document.fullscreenElement === rootRef.current) {
      await document.exitFullscreen?.();
      return;
    }
    await rootRef.current.requestFullscreen?.();
  };

  const center = hasWork ? toLatLng(workCenter) : toLatLng(homeCenter);
  const mapColorScheme = resolveMapColorScheme(mapLayer, isDarkTheme);

  return (
    <div ref={rootRef} className={`map-view${isFullscreen ? ' map-view--fullscreen' : ''}`}>
      {/* APIProvider is mounted in App.js so it persists across page navigation. */}
      <Map
          defaultCenter={center}
          defaultZoom={MAP_DEFAULT_ZOOM}
          mapId={MAP_ID}
          renderingType="VECTOR"
          colorScheme={mapColorScheme}
          style={{ width: '100%', height: '100%' }}
          gestureHandling="greedy"
          defaultMapTypeId={resolveGoogleMapTypeId(mapLayer)}
          mapTypeControl={false}
          zoomControl={false}
          fullscreenControl={false}
          streetViewControl={false}
          clickableIcons={false}
        >
          {/* All components below use useMap() and MUST be inside <Map> */}
          <MapLoadReporter onMapReady={handleMapReady} searchKey={searchKey} />
          <MapClickHandler onMapClick={handleMapTap} />
          {openMarkerKey === LOCATION_PICK_POPUP_KEY && (
            <LocationPickPopup
              point={pickedPoint}
              coverageAreaScores={showCrimeLayer ? coverageAreaScores : null}
              hideSafetyScore={hideSafetyScore}
              hideAffordabilityScore={hideAffordabilityScore}
              hideLivabilityScore={hideLivabilityScore}
              onUseLocation={onZctaUseLocation}
              onClose={closePopup}
            />
          )}
          <SyncColorScheme colorScheme={mapColorScheme} />
          <BikeLayer
            enabled={showBikeLayer}
            onLoadingProgress={handleBikeLoadingProgress}
          />
          <ZctaCrimeLayer
            enabled={showCrimeLayer}
            zctaData={zctaData}
            fullGeoJson={fullGeoJson}
            workCenter={workCenter}
            homeCenter={homeCenter}
            opacity={zctaLayerOpacity}
            onAreaClick={handleMapTap}
          />
          <SyncMapType mapLayer={mapLayer} />
          <FocusedMarkerController focusedMarker={focusedMarker} />
          {showThingsToDo && amenityVisibility.hotSprings && hotSprings && hotSprings.length > 0 && (
            <HotSpringLayer
              springs={hotSprings}
              openMarkerKey={openMarkerKey}
              onOpenMarker={setOpenMarkerKey}
            />
          )}
          {showThingsToDo && amenityVisibility.skiResorts && skiResorts && skiResorts.length > 0 && (
            <SkiResortLayer
              resorts={skiResorts}
              openMarkerKey={openMarkerKey}
              onOpenMarker={setOpenMarkerKey}
            />
          )}
          <MapControlsBar
            workCenter={workCenter}
            homeCenter={homeCenter}
            mapLayer={mapLayer}
            onMapLayerChange={onMapLayerChange}
            onOpenFilters={onOpenFilters}
            showBikeLayer={showBikeLayer}
            onBikeLayerChange={setShowBikeLayer}
            showSafetyLayer={showCrimeLayer}
            onSafetyLayerChange={onShowCrimeLayerChange}
            safetyOpacity={zctaLayerOpacity}
            onSafetyOpacityChange={onZctaLayerOpacityChange}
            showSafetyControls={showCrimeControls}
            isFullscreen={isFullscreen}
            onFullscreenToggle={handleFullscreenToggle}
          />

          <AmenityMarkersLayer
            amenityMarkerGroups={amenityMarkerGroups}
            openMarkerKey={openMarkerKey}
            onOpenMarker={setOpenMarkerKey}
          />
          <ListingMarkersLayer
            showFFMarkers={effectiveShowFFMarkers}
            ffListings={ffListings}
            openMarkerKey={openMarkerKey}
            onOpenMarker={setOpenMarkerKey}
            onRenderProgress={onListingsRenderProgress}
          />
          {shouldLoadTenantProperties && (
            <TenantPropertiesLayer
              properties={tenantProperties}
              openMarkerKey={openMarkerKey}
              onOpenMarker={setOpenMarkerKey}
              inactiveUrls={inactiveTenantListings}
              onToggleInactive={onToggleTenantListingInactive}
              favoriteUrls={favoriteTenantListings}
              onToggleFavorite={onToggleTenantListingFavorite}
            />
          )}
          <AddressMarkersLayer
            hasWork={hasWork}
            hasHome={hasHome}
            workCenter={workCenter}
            homeCenter={homeCenter}
            workAddress={workAddress}
            homeAddress={homeAddress}
            openMarkerKey={openMarkerKey}
            onOpenMarker={setOpenMarkerKey}
          />
      </Map>
      {bikeLoadingProgress !== null && (
        <BikeLoadingBar progress={bikeLoadingProgress} />
      )}
    </div>
  );
}

function areMapViewPropsEqual(prevProps, nextProps) {
  const keys = new Set([...Object.keys(prevProps), ...Object.keys(nextProps)]);
  for (const key of keys) {
    const prevValue = prevProps[key];
    const nextValue = nextProps[key];

    if (typeof prevValue === 'function' && typeof nextValue === 'function') {
      continue;
    }

    if (!Object.is(prevValue, nextValue)) {
      return false;
    }
  }

  return true;
}

const MapView = memo(MapViewComponent, areMapViewPropsEqual);
MapView.displayName = 'MapView';

export default MapView;

